import type { FieldConfig, FieldValue, FieldValues } from '@shared/types/interactiveFields';

/**
 * В какво състояние е едно поле спрямо РЕШЕНИЕТО, което иска от родителя.
 *
 * Изнесено от рендера, за да е тестваемо само и да е на ЕДНО място: същата
 * преценка трябва да управлява и маркировката на полето, и брояча „остават N".
 * Разминат ли се двете, броячът ще обещава решения, които екранът не показва
 * къде са — а това е по-лошо от липсващ брояч.
 *
 * 🔴 Правилото зад стойностите: **оцветява се само онова, което иска действие.**
 * Решените утихват. Ако решените са зелени, а нерешените жълти, екранът е шарен
 * и трябва да се ЧЕТАТ цветовете; ако решените са тихи, окото намира оставащите
 * без да мисли. Целта е „какво остава", не „какво има".
 */
export type DecisionState =
  /** Има съобщение за грешка — то печели над всичко останало. */
  | 'error'
  /** Задължително и празно: единственото, което иска действие СЕГА. */
  | 'pending'
  /** Отговорено — независимо дали отговорът е „да" или „не". */
  | 'answered'
  /** Незадължително и празно: няма какво да се иска. */
  | 'optional';

/**
 * Празна ли е стойността — по СЪЩИТЕ правила като валидацията.
 *
 * ⚠️ Съгласувано с `useFieldValidation.validateField`: `undefined`/`null`/`''`
 * са празни за всеки тип, а `checkbox` е празен и при `false`. Разминаване тук
 * би дало поле, което броячът смята за решено, а изпращането отказва — или
 * обратното.
 */
export function isEmptyValue(field: FieldConfig, value: FieldValue): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (field.type === 'checkbox') return !value;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export function fieldDecisionState(
  field: FieldConfig,
  value: FieldValue,
  error?: string,
): DecisionState {
  if (error) return 'error';
  const empty = isEmptyValue(field, value);
  if (!field.required) return empty ? 'optional' : 'answered';
  return empty ? 'pending' : 'answered';
}

/**
 * Колко решения ОСТАВАТ, и колко се искат общо.
 *
 * 🔴 Броячът показва ОСТАТЪКА, не общия брой. „Остават 3 от 4" отговаря на
 * въпроса, който родителят има в дълъг документ („мога ли вече да подпиша"),
 * докато „4 решения" отговаря на въпрос, който никой не задава.
 *
 * ⚠️ `error` се брои за оставащо: поле с грешка още иска действие, макар да не
 * е празно. Иначе броячът би казал „нула остават", а подписването да откаже.
 */
export function countPendingDecisions(
  fields: FieldConfig[],
  values: FieldValues,
  errors: Record<string, string> = {},
): { pending: number; total: number } {
  let pending = 0;
  let total = 0;
  for (const field of fields) {
    if (!field.required) continue;
    total += 1;
    const state = fieldDecisionState(field, values[field.id], errors[field.id]);
    if (state === 'pending' || state === 'error') pending += 1;
  }
  return { pending, total };
}
